import {
  validId,
} from './daily-routine.ts';

export const RESTORE_STATUSES = [
  'BUILDING',
  'VALIDATED',
  'APPLYING',
  'SWITCHING',
  'VERIFYING',
  'ROLLING_BACK',
  'VERIFYING_ROLLBACK',
  'COMPLETED',
  'ROLLED_BACK',
  'ABANDONED',
] as const;

export type RestoreStatus =
  typeof RESTORE_STATUSES[number];

export type RestoreFailureCode =
  | 'RESTORE_INVALID_BACKUP'
  | 'RESTORE_SCHEMA_FUTURE'
  | 'RESTORE_PREVIEW_STALE'
  | 'RESTORE_IN_PROGRESS'
  | 'RESTORE_OPERATION_CONFLICT'
  | 'RESTORE_CORRUPTION'
  | 'STORAGE_QUOTA'
  | 'TRANSACTION_ABORTED'
  | 'STORAGE_UNAVAILABLE';

export interface RestorePreviewToken {
  activeDatasetId: string;
  datasetRevision: number;
  controlEpoch: number;
  schemaVersion: 1;
  restoreOperationId: string;
  stagingDatasetId: string;
}

export interface RetiredCleanupCandidate {
  datasetId: string;

  reason:
    | 'REPLACED_BY_SUCCESS'
    | 'FAILED_TARGET_AFTER_ROLLBACK'
    | 'ABANDONED_STAGING';

  retiredAt: string;
}

export interface RestoreOperation {
  restoreOperationId: string;
  backupId: string;
  stagingDatasetId: string;

  status: RestoreStatus;

  createdAt: string;
  updatedAt: string;

  preview?: RestorePreviewToken;

  sourceDatasetId?: string;

  completedAt?: string;
  successDatasetId?: string;

  rolledBackAt?: string;
  recoveredDatasetId?: string;

  abandonedAt?: string;

  retiredCleanupCandidate?:
    RetiredCleanupCandidate;
}

function validNatural(
  value: unknown,
): value is number {
  return (
    typeof value === 'number'
    && Number.isSafeInteger(value)
    && value >= 0
  );
}

function validInstant(
  value: unknown,
): value is string {
  return (
    typeof value === 'string'
    && value.length > 0
    && Number.isFinite(
      Date.parse(value),
    )
  );
}

export function isRestoreStatus(
  value: unknown,
): value is RestoreStatus {
  return (
    typeof value === 'string'
    && (
      RESTORE_STATUSES as readonly string[]
    ).includes(value)
  );
}

export function isTerminalRestoreStatus(
  status: RestoreStatus,
) {
  return (
    status === 'COMPLETED'
    || status === 'ROLLED_BACK'
    || status === 'ABANDONED'
  );
}

export function canTransitionRestore(
  from: RestoreStatus,
  to: RestoreStatus,
) {
  switch (from) {
    case 'BUILDING':
      return (
        to === 'VALIDATED'
        || to === 'ABANDONED'
      );

    case 'VALIDATED':
      return (
        to === 'APPLYING'
        || to === 'ABANDONED'
      );

    case 'APPLYING':
      return to === 'SWITCHING';

    case 'SWITCHING':
      return to === 'VERIFYING';

    case 'VERIFYING':
      return (
        to === 'COMPLETED'
        || to === 'ROLLING_BACK'
      );

    case 'ROLLING_BACK':
      return to === 'VERIFYING_ROLLBACK';

    case 'VERIFYING_ROLLBACK':
      return to === 'ROLLED_BACK';

    case 'COMPLETED':
    case 'ROLLED_BACK':
    case 'ABANDONED':
      return false;
  }
}

export function validRestorePreviewToken(
  value: unknown,
): value is RestorePreviewToken {
  if (
    typeof value !== 'object'
    || value === null
  ) {
    return false;
  }

  const token =
    value as Partial<RestorePreviewToken>;

  return (
    validId(token.activeDatasetId)
    && validNatural(token.datasetRevision)
    && validNatural(token.controlEpoch)
    && token.schemaVersion === 1
    && validId(token.restoreOperationId)
    && validId(token.stagingDatasetId)
  );
}

function validCandidate(
  value: unknown,
): value is RetiredCleanupCandidate {
  if (
    typeof value !== 'object'
    || value === null
  ) {
    return false;
  }

  const candidate =
    value as Partial<RetiredCleanupCandidate>;

  return (
    validId(candidate.datasetId)
    && (
      candidate.reason
        === 'REPLACED_BY_SUCCESS'
      || candidate.reason
        === 'FAILED_TARGET_AFTER_ROLLBACK'
      || candidate.reason
        === 'ABANDONED_STAGING'
    )
    && validInstant(candidate.retiredAt)
  );
}

export function validRestoreOperation(
  value: unknown,
): value is RestoreOperation {
  if (
    typeof value !== 'object'
    || value === null
  ) {
    return false;
  }

  const operation =
    value as Partial<RestoreOperation>;

  if (
    !validId(operation.restoreOperationId)
    || !validId(operation.backupId)
    || !validId(operation.stagingDatasetId)
    || !isRestoreStatus(operation.status)
    || !validInstant(operation.createdAt)
    || !validInstant(operation.updatedAt)
  ) {
    return false;
  }

  if (
    operation.preview !== undefined
    && !validRestorePreviewToken(
      operation.preview,
    )
  ) {
    return false;
  }

  if (
    operation.sourceDatasetId !== undefined
    && !validId(operation.sourceDatasetId)
  ) {
    return false;
  }

  if (
    operation.completedAt !== undefined
    && !validInstant(operation.completedAt)
  ) {
    return false;
  }

  if (
    operation.successDatasetId !== undefined
    && !validId(operation.successDatasetId)
  ) {
    return false;
  }

  if (
    operation.rolledBackAt !== undefined
    && !validInstant(operation.rolledBackAt)
  ) {
    return false;
  }

  if (
    operation.recoveredDatasetId !== undefined
    && !validId(operation.recoveredDatasetId)
  ) {
    return false;
  }

  if (
    operation.abandonedAt !== undefined
    && !validInstant(operation.abandonedAt)
  ) {
    return false;
  }

  if (
    operation.retiredCleanupCandidate
      !== undefined
    && !validCandidate(
      operation.retiredCleanupCandidate,
    )
  ) {
    return false;
  }

  /*
   * Terminal states have explicit, non-ambiguous meaning.
   * This prevents a journal row from claiming success and
   * rollback at the same time.
   */
  if (operation.status === 'COMPLETED') {
    return (
      validInstant(operation.completedAt)
      && validId(operation.successDatasetId)
      && operation.rolledBackAt === undefined
      && operation.recoveredDatasetId === undefined
      && operation.abandonedAt === undefined
    );
  }

  if (operation.status === 'ROLLED_BACK') {
    return (
      validInstant(operation.rolledBackAt)
      && validId(operation.recoveredDatasetId)
      && operation.completedAt === undefined
      && operation.successDatasetId === undefined
      && operation.abandonedAt === undefined
    );
  }

  if (operation.status === 'ABANDONED') {
    return (
      validInstant(operation.abandonedAt)
      && operation.completedAt === undefined
      && operation.rolledBackAt === undefined
      && operation.successDatasetId === undefined
      && operation.recoveredDatasetId === undefined
    );
  }

  return (
    operation.completedAt === undefined
    && operation.successDatasetId === undefined
    && operation.rolledBackAt === undefined
    && operation.recoveredDatasetId === undefined
    && operation.abandonedAt === undefined
  );
}

export type RestoreResult<T> =
  | {
      ok: true;
      value: T;
    }
  | {
      ok: false;
      code: RestoreFailureCode;
    };

export interface RestoreApplyOutcome {
  status:
    | 'COMPLETED'
    | 'ROLLED_BACK';

  activeDatasetId: string;
}

export interface RestoreRecoveryOutcome {
  status:
    | 'IDLE'
    | 'PREVIEW_PENDING'
    | 'RECOVERED_COMPLETED'
    | 'RECOVERED_ROLLED_BACK';
}
